import React, { useMemo, useState, useEffect, useRef } from "react";
import styles from "./index.module.scss";
import { Form, Input, Modal, Cascader, Radio, message, Spin } from "antd";
import { CompanyTag } from "@/components/company-tag";
import { Tag } from "@/components";
import { EllipsisText, Icon } from "@terminus/dqh-web-ui";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEY } from "@/utils";
import { getCategories, getCityAreas } from "@/services";
import {
  getBusinessCard,
  updateBusinessCard,
  sendBusinessCard,
} from "@/services/business";
import { useParams } from "react-router-dom";

interface CategoryItem {
  name: string;
  id: string;
  children?: CategoryItem[];
}

interface AreaItem {
  name: string;
  code: string;
  children?: AreaItem[];
}

const convertToCascaderOptions = (categories: CategoryItem[]): any[] => {
  return categories.map((category) => ({
    label: category.name,
    value: category.id,
    children: category.children
      ? convertToCascaderOptions(category.children)
      : undefined,
    isLeaf: !category.children || category.children.length === 0,
  }));
};

const convertToAreaCascaderOptions = (areas: AreaItem[], level: number = 1): any[] => {
  const maxLevel = 3;
  return areas.map((area) => ({
    label: area.name,
    value: area.code,
    children: area.children && level < maxLevel
      ? convertToAreaCascaderOptions(area.children, level + 1)
      : undefined,
    isLeaf: !area.children || area.children.length === 0 || level >= maxLevel,
  }));
};

interface RecommendOnselfModalProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  subjuct?: string;
  memberId: string;
}

export const RecommendOnselfModal: React.FC<RecommendOnselfModalProps> = ({
  visible,
  setVisible,
  subjuct='',
  memberId,
}) => {
  const { opportunityId: id } = useParams();
  const [supplierInfo, setSupplierInfo] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [changeModalLoading, setChangeModalLoading] = useState(false);
  const [form] = Form.useForm();
  const [changeForm] = Form.useForm();
  const [showChangeModal, setShowChangeModal] = useState(false);
  const isInitializingRef = useRef(false);
  const [modifiedContactInfo, setModifiedContactInfo] = useState<{
    contactName?: string;
    contactPhone?: string;
    supplyCateList?: any[];
    supplyAreaList?: any[];
    supplyAreaType?: string;
  } | null>(null);
  useEffect(() => {
    if (visible && (id||memberId)) {
      setLoading(true);
      getBusinessCard({opportunityId:id,buyerMemberId:memberId})
        .then((res:any) => {
          if (modifiedContactInfo) {
            setSupplierInfo({
              ...res,
              ...modifiedContactInfo,
            });
          } else {
            setSupplierInfo(res);
          }
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, id, memberId]);
  const supplyAreaType = Form.useWatch("supplyAreaType", changeForm);
  const prevSupplyAreaTypeRef = useRef<string | undefined>();
  useEffect(() => {
    if (
      supplyAreaType === "area" && 
      !isInitializingRef.current &&
      prevSupplyAreaTypeRef.current === "nation"
    ) {
      changeForm.setFieldsValue({
        supplyAreaList: [],
      });
    }
    prevSupplyAreaTypeRef.current = supplyAreaType;
  }, [supplyAreaType, changeForm]);
  const {
    data: categories = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: [QUERY_KEY.getCategories],
    queryFn: getCategories,
    staleTime: Infinity,
  });

  const {
    data: areas = [],
    isLoading: areasLoading,
    error: areasError,
  } = useQuery({
    queryKey: [QUERY_KEY.getCityAreas, { areaType: 'PROVINCE' }],
    queryFn: () => getCityAreas({ areaType: 'PROVINCE' }),
    staleTime: Infinity,
  });

  const cascaderOptions = useMemo(() => {
    return convertToCascaderOptions(categories);
  }, [categories]);

  const areaCascaderOptions = useMemo(() => {
    return convertToAreaCascaderOptions(areas);
  }, [areas]);

  const convertCateListToCascaderValue = (cateList: any[]): any[] => {
    if (!cateList || !Array.isArray(cateList) || cateList.length === 0) {
      return [];
    }

    const findValuePath = (
      options: any[],
      targetId: string | number,
      currentPath: any[] = []
    ): any[] | null => {
      for (const option of options) {
        const newPath = [...currentPath, option.value];
        if (String(option.value) === String(targetId) || option.value === targetId) {
          return newPath;
        }
        if (option.children) {
          const found = findValuePath(option.children, targetId, newPath);
          if (found) return found;
        }
      }
      return null;
    };

    const buildPathFromParentId = (item: any, allItems: any[]): any[] | null => {
      const targetId = item.categoryCode || item.id || item.value;
      if (!targetId) return null;
      if (item.parentId === null || item.parentId === undefined) {
        const path = findValuePath(cascaderOptions, targetId);
        return path;
      }
      const parentItem = allItems.find((it) => {
        const itId = it.categoryCode || it.id || it.value;
        return (
          String(itId) === String(item.parentId) ||
          itId === item.parentId
        );
      });
      
      if (parentItem) {
        const parentPath = buildPathFromParentId(parentItem, allItems);
        if (parentPath && parentPath.length > 0) {
          return [...parentPath, targetId];
        }
      }
      return findValuePath(cascaderOptions, targetId);
    };

    const findLeafNode = (rootItem: any, allItems: any[]): any | null => {
      const rootId = rootItem.categoryCode || rootItem.id || rootItem.value;
      if (!rootId) return null;
      const childItem = allItems.find((it) => {
        const itParentId = it.parentId;
        return (
          itParentId !== null &&
          itParentId !== undefined &&
          (String(itParentId) === String(rootId) || itParentId === rootId)
        );
      });
      if (childItem) {
        return findLeafNode(childItem, allItems);
      }
      return rootItem;
    };
    const rootNodes = cateList.filter(
      (item) => item.parentId === null || item.parentId === undefined
    );
    const result: any[] = [];
    
    if (rootNodes.length > 0) {
      rootNodes.forEach((rootNode) => {
        const leafNode = findLeafNode(rootNode, cateList);
        if (leafNode) {
          const fullPath = buildPathFromParentId(leafNode, cateList);
          if (fullPath && fullPath.length > 0) {
            result.push(fullPath);
          }
        }
      });
    } else {
      cateList.forEach((item) => {
        const fullPath = buildPathFromParentId(item, cateList);
        if (fullPath && fullPath.length > 0) {
          result.push(fullPath);
        }
      });
    }
    
    return result;
  };

  useEffect(() => {
    if (visible && supplierInfo && cascaderOptions && cascaderOptions.length > 0) {
      const { intendedCooperationCategoryList} = supplierInfo;
      let cascaderValue: any[] = [];
      if (intendedCooperationCategoryList && Array.isArray(intendedCooperationCategoryList) && intendedCooperationCategoryList.length > 0) {
        if (Array.isArray(intendedCooperationCategoryList[0])) {
          cascaderValue = intendedCooperationCategoryList;
        } else {
          cascaderValue = convertCateListToCascaderValue(intendedCooperationCategoryList);
        }
      }
      if (cascaderValue.length > 0) {
        form.setFieldsValue({
          intendedCooperationCategoryList: cascaderValue,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, supplierInfo, cascaderOptions]);

  const convertAreaListToCascaderValue = (areaList: any[]): any[] => {
    if (!areaList || !Array.isArray(areaList) || areaList.length === 0) {
      return [];
    }
    if (!areaCascaderOptions || areaCascaderOptions.length === 0) {
      return [];
    }
    const findValuePath = (
      options: any[],
      targetId: string,
      currentPath: any[] = []
    ): any[] | null => {
      for (const option of options) {
        const newPath = [...currentPath, option.value];
        if (option.value === targetId) {
          return newPath;
        }
        if (option.children && option.children.length > 0) {
          const found = findValuePath(option.children, targetId, newPath);
          if (found) return found;
        }
      }
      return null;
    };

    return areaList
      .map((item) => {
        const targetId = item.value || item.code;
        if (!targetId) return null;
        let path = findValuePath(areaCascaderOptions, targetId);
        if (!path && item.parent) {
          const parentPath = findValuePath(areaCascaderOptions, item.parent);
          if (parentPath) {
            path = [...parentPath, targetId];
          }
        }
        if (!path) {
          path = [targetId];
        }
        
        return path;
      })
      .filter((path): path is any[] => path !== null && path.length > 0);
  };

  useEffect(() => {
    if (
      showChangeModal &&
      supplierInfo &&
      Object.keys(supplierInfo).length > 0
    ) {
      isInitializingRef.current = true;
      const formValues = { ...supplierInfo,supplyAreaList:supplierInfo?.supplyAreaList?.map((item:any) => ({...item,value:item.value?.replace('DIV_','')})) };
      if (
        formValues.supplyCateList &&
        Array.isArray(formValues.supplyCateList)
      ) {
        if (
          formValues.supplyCateList.length > 0 &&
          typeof formValues.supplyCateList[0] === "object" &&
          !Array.isArray(formValues.supplyCateList[0])
        ) {
          formValues.supplyCateList = convertCateListToCascaderValue(
            formValues.supplyCateList
          );
        }
      }
      if (
        formValues.supplyAreaList &&
        Array.isArray(formValues.supplyAreaList)
      ) {
        if (
          formValues.supplyAreaList.length > 0 &&
          typeof formValues.supplyAreaList[0] === "object" &&
          !Array.isArray(formValues.supplyAreaList[0])
        ) {
          formValues.supplyAreaList = convertAreaListToCascaderValue(
            formValues.supplyAreaList
          );
        }
      }

      changeForm.setFieldsValue(formValues);
      setTimeout(() => {
        isInitializingRef.current = false;
      }, 0);
    }
  }, [showChangeModal, supplierInfo, changeForm, cascaderOptions, areaCascaderOptions]);

  const handleChangeBtn = () => {
    setVisible(false);
    setShowChangeModal(true);
  };
  const handleCancelChangeModal = () => {
    setShowChangeModal(false);
    const formValues = { ...supplierInfo };
    if (formValues.supplyCateList && Array.isArray(formValues.supplyCateList)) {
      if (
        formValues.supplyCateList.length > 0 &&
        typeof formValues.supplyCateList[0] === "object" &&
        !Array.isArray(formValues.supplyCateList[0])
      ) {
        formValues.supplyCateList = convertCateListToCascaderValue(
          formValues.supplyCateList
        );
      }
    }
    if (
      formValues.supplyAreaList &&
      Array.isArray(formValues.supplyAreaList)
    ) {
      if (
        formValues.supplyAreaList.length > 0 &&
        typeof formValues.supplyAreaList[0] === "object" &&
        !Array.isArray(formValues.supplyAreaList[0])
      ) {
        formValues.supplyAreaList = convertAreaListToCascaderValue(
          formValues.supplyAreaList
        );
      }
    }
    changeForm.setFieldsValue(formValues);
    setVisible(true);
  };
  const findLabelPath = (
    options: any[],
    path: any[],
    currentPath: string[] = []
  ): string => {
    if (path.length === 0) return currentPath.join("/");
    const currentValue = path[0];
    const option = options.find((opt) => opt.value === currentValue);
    if (option) {
      const newPath = [...currentPath, option.label];
      if (path.length === 1) {
        return newPath.pop() || "";
      }
      if (option.children) {
        return findLabelPath(option.children, path.slice(1), newPath) || "";
      }
    }
    return currentPath.pop() || "";
  };

  const convertToCategoryTree = (valuePaths: any[]): any[] => {
    if (!valuePaths || valuePaths.length === 0) return [];
    const nodeMap = new Map<string, any>();
    valuePaths.forEach((path: any[]) => {
      if (!path || path.length === 0) return;
      let currentMap = nodeMap;
      let currentOptions = cascaderOptions;
      path.forEach((code: string, index: number) => {
        const option = currentOptions.find((opt: any) => opt.value === code);
        if (!option) return;
        
        const nodeKey = code;
        if (!currentMap || typeof currentMap.has !== 'function') {
          return;
        }
        
        if (!currentMap.has(nodeKey)) {
          const newNode = {
            categoryName: option.label,
            categoryCode: code,
            children: new Map<string, any>(),
          };
          currentMap.set(nodeKey, newNode);
        }
        
        const currentNode = currentMap.get(nodeKey);
        if (!currentNode) return;
        
        if (index === path.length - 1) {
          currentNode.children = null;
        } else {
          if (!currentNode.children || !(currentNode.children instanceof Map)) {
            currentNode.children = new Map<string, any>();
          }
          currentMap = currentNode.children;
          currentOptions = option.children || [];
        }
      });
    });
    const convertMapToArray = (map: Map<string, any>): any[] => {
      return Array.from(map.values()).map((node) => {
        const result: any = {
          categoryName: node.categoryName,
          categoryCode: node.categoryCode,
        };
        if (node.children instanceof Map) {
          result.children = convertMapToArray(node.children);
        } else if (node.children !== null) {
          result.children = node.children;
        }
        return result;
      });
    };
    
    return convertMapToArray(nodeMap);
  };

  const handleSendRecommendOnself = async () => {
    let values = await form.validateFields();
    let intendedCooperationCategoryList =
      values.intendedCooperationCategoryList;
    let intendedCooperationCategoryJson: any[] = [];
    
    if (
      values.intendedCooperationCategoryList &&
      values.intendedCooperationCategoryList.length > 0
    ) {
      intendedCooperationCategoryList =
        values.intendedCooperationCategoryList.map((valuePath: any[]) => {
          const pathCopy = [...valuePath];
          return {
            categoryCode: pathCopy.pop(),
            categoryName: findLabelPath(cascaderOptions, valuePath),
          };
        });
      intendedCooperationCategoryJson = convertToCategoryTree(
        values.intendedCooperationCategoryList
      );
    }
    
    values.intendedCooperationCategoryList = intendedCooperationCategoryList;
    values.intendedCooperationCategoryJson = intendedCooperationCategoryJson;
    const param = { ...supplierInfo, ...values, opportunityId: id, buyerMemberId:memberId };
    await sendBusinessCard(param);
    message.success("发送成功");
    setVisible(false);
    form.resetFields();
    changeForm.resetFields();
    setModifiedContactInfo(null);
  };

  const handleChangeModalOk = async () => {
    let values = await changeForm.validateFields();
    let supplyAreaData = values.supplyAreaData;
    if (
      !supplyAreaData &&
      values.supplyAreaList &&
      values.supplyAreaList.length > 0 &&
      supplyAreaType === "area"
    ) {
      supplyAreaData = values.supplyAreaList.map((valuePath: any[]) => {
        const pathCopy = [...valuePath];
        return {
          value: pathCopy.pop(),
          label: findLabelPath(areaCascaderOptions, valuePath),
        };
      });
    }
    values.supplyAreaList = supplyAreaData?.map((item:any) => ({...item,value:'DIV_'+item.value}));
    let supplyCateListData = values.supplyCateListData;
    if (
      !supplyCateListData &&
      values.supplyCateList &&
      values.supplyCateList.length > 0
    ) {
      supplyCateListData = values.supplyCateList.map((valuePath: any[]) => {
        const pathCopy = [...valuePath];
        return {
          categoryCode: pathCopy.pop(),
          categoryName: findLabelPath(cascaderOptions, valuePath),
        };
      });
    }
    values.supplyCateList = supplyCateListData;
    await updateBusinessCard(values);
    setChangeModalLoading(true);
    setTimeout(async () => {
      const modifiedInfo = {
        contactName: values.contactName,
        contactPhone: values.contactPhone,
        supplyCateList: supplyCateListData,
        supplyAreaList: supplyAreaData,
        supplyAreaType: values.supplyAreaType,
      };
      setModifiedContactInfo(modifiedInfo);
      setSupplierInfo((prev: any) => ({
        ...prev,
        ...modifiedInfo,
      }));
      message.success("修改成功");
      setShowChangeModal(false);
      changeForm.resetFields();
      setChangeModalLoading(false);
      setLoading(true);
      form.resetFields();
      setVisible(true);
    }, 2000);
  };

  return (
    <>
      {visible && (
        <Modal
          open={visible}
          onCancel={() => {
            setVisible(false);
            form.resetFields();
            setModifiedContactInfo(null);
          }}
          centered
          title="供应商自荐"
          width={560}
          wrapClassName={styles.recommendOnselfModal}
          okText="确认发送"
          onOk={handleSendRecommendOnself}
          confirmLoading={loading}
        >
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin size="large" />
            </div>
          ) : (
            <>
          <div className={styles.supplierInfo}>
            <div className={styles.supplierInfoContent}>
              <div>
                <div className={styles.supplierCompanyName}>
                  {supplierInfo?.companyName || '-'}
                </div>
                <div className={styles.supplierTags}>
                  {!!supplierInfo?.payedSince && (
                    <CompanyTag
                      year={supplierInfo?.payedSince || 0}
                      payedLevel={supplierInfo?.payedLevel}
                    />
                  )}
                  {!!supplierInfo?.realNameAuthed ? (
                    <Tag
                      type="info"
                      title="已实名"
                      icon="yishiming"
                      style={{ marginBottom: 0 }}
                    />
                  ) : null}
                  {!!supplierInfo?.memberTaxType && (
                    <Tag
                      icon="yibannashuiren"
                      type="info"
                      title="一般纳税人"
                      style={{ marginBottom: 0 }}
                    />
                  )}
                </div>
              </div>
              <div className={styles.modifyCardName} onClick={handleChangeBtn}>
                修改名片
              </div>
            </div>
            <div className={styles.supplierPersonInfo}>
              {supplierInfo?.contactName || "-"}
              <div className={styles.personInfoSeparator}>/</div>
              {supplierInfo?.contactPhone || "-"}
            </div>
            <div className={styles.supplierInfoGroup}>
              <div className={styles.supplierInfoGroupItem}>
                <div className={styles.supplierInfoGroupItemTitle}>
                  <Icon color="#777779" name="bendilocal-two" size={12} />
                  主营品类
                </div>
                <div style={{ flex: 1 }}>
                  <EllipsisText
                    text={
                      supplierInfo?.supplyCateList?.length > 0
                        ? supplierInfo?.supplyCateList
                            ?.map((item: any) => item.categoryName)
                            .join(",")
                        : "-"
                    }
                    style={{
                      fontSize: 13,
                      fontWeight: "normal",
                      lineHeight: "20px",
                      color: "#1D1D1F",
                    }}
                    adaptWidth={true}
                  />
                </div>
              </div>
              <div className={styles.supplierInfoGroupItem}>
                <div className={styles.supplierInfoGroupItemTitle}>
                  <Icon color="#777779" name="jinrongfinance" size={12} />
                  报价品类
                </div>
                <div style={{ flex: 1, overflow: "hidden" }}>
                  <EllipsisText
                    text={
                      supplierInfo?.quotationCategory || "-"
                    }
                    adaptWidth={true}
                    style={{
                      fontSize: 13,
                      fontWeight: "normal",
                      lineHeight: "20px",
                      color: "#1D1D1F",
                    }}
                  />
                </div>
              </div>
              <div className={styles.supplierInfoGroupItem}>
                <div className={styles.supplierInfoGroupItemTitle}>
                  <Icon color="#777779" name="shijiantime" size={12} />
                  供货地区
                </div>
                <div style={{ flex: 1, overflow: "hidden" }}>
                  <EllipsisText
                    text={
                      supplierInfo?.supplyAreaType === "nation"
                        ? "全国"
                        : supplierInfo?.supplyAreaList?.length > 0
                        ? supplierInfo?.supplyAreaList
                            ?.map((item: any) => item.label)
                            .join(",")
                        : "-"
                    }
                    adaptWidth={true}
                    style={{
                      fontSize: 13,
                      fontWeight: "normal",
                      lineHeight: "20px",
                      color: "#1D1D1F",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className={styles.recommendOnselfForm}>
            <Form layout="vertical" form={form}>
              <Form.Item
                label="意向合作类目"
                name="intendedCooperationCategoryList"
              >
                <Cascader
                  className={styles.cascader}
                  options={cascaderOptions}
                  disabled={isLoading || !!error}
                  placeholder="请选择"
                  maxTagCount="responsive"
                  allowClear
                  multiple
                  expandTrigger="hover"
                  showSearch={{
                    filter: (inputValue, path) => {
                      return path.some(
                        (option) =>
                          (option.label as string)
                            .toLowerCase()
                            .indexOf(inputValue.toLowerCase()) > -1
                      );
                    },
                  }}
                  dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Form.Item 
                label="合作留言" 
                name="cooperationMessage"
                initialValue={subjuct?`您发布的【${subjuct}】我司能响应，有兴趣可以联系我们`:''}
              >
                <Input.TextArea
                  placeholder="请输入"
                  maxLength={100}
                  showCount
                  style={{ height: 124 }}
                />
              </Form.Item>
            </Form>
          </div>
          </>
          )}
        </Modal>
      )}
      {showChangeModal && (
        <Modal
          centered
          open={showChangeModal}
          onCancel={handleCancelChangeModal}
          onOk={handleChangeModalOk}
          title="信息修改"
          wrapClassName={styles.changeModal}
          confirmLoading={changeModalLoading}
        >
          <Form form={changeForm} layout="vertical">
            <Form.Item label="联系人" name="contactName">
              <Input placeholder="请输入联系人" />
            </Form.Item>
            <Form.Item label="联系方式" name="contactPhone">
              <Input placeholder="请输入联系人" />
            </Form.Item>
            <Form.Item label="主营品类" name="supplyCateList">
              <Cascader
                options={cascaderOptions}
                disabled={isLoading || !!error || !supplierInfo?.isAuth}
                placeholder="请选择"
                maxTagCount="responsive"
                allowClear
                multiple
                expandTrigger="hover"
              />
            </Form.Item>
            <Form.Item label="供货范围" name="supplyAreaType">
              <Radio.Group disabled={!supplierInfo?.isAuth}>
                <Radio value="nation">全国</Radio>
                <Radio value="area">区域</Radio>
              </Radio.Group>
            </Form.Item>
            {supplyAreaType === "area" && (
              <Form.Item
                label="供货地区"
                name="supplyAreaList"
                rules={[
                  {
                    required: supplyAreaType === "area",
                    message: "请选择供货地区",
                  },
                ]}
              >
                <Cascader
                  options={areaCascaderOptions}
                  disabled={areasLoading || !!areasError || !supplierInfo?.isAuth}
                  placeholder="请选择"
                  maxTagCount="responsive"
                  allowClear
                  multiple
                  expandTrigger="hover"
                  showSearch={{
                    filter: (inputValue, path) => {
                      return path.some(
                        (option) =>
                          (option.label as string)
                            .toLowerCase()
                            .indexOf(inputValue.toLowerCase()) > -1
                      );
                    },
                  }}
                  dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            )}
          </Form>
        </Modal>
      )}
    </>
  );
};
